import type { CreateShiftChangesParams } from '@src/models/interfaces/attendanceEvent.interface';
import { SHIFT_CHANGE_TYPE } from '@src/models/interfaces/attendanceEvent.interface';
import { ATTENDANCE_LEAVE_TYPE } from '@src/models/interfaces/attendanceLeaveType';
import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type { IWorkJourneyMatrixCell } from '@src/models/interfaces/workJourneyManagement.interface';

import { mapLeaveTypeToMatrixCell } from './matrixCellDisplay';

/**
 * Attendance edits are applied by a Pub/Sub trigger, so neither the attendance
 * record nor the Elasticsearch-backed matrix reflect them right away. These
 * helpers project the submitted change onto the loaded data so the user sees
 * the edit immediately; the next reload replaces it with server truth.
 */

type TimestampLike = { _seconds: number; _nanoseconds: number };

/** Cell fields the optimistic patch touches (matrix cell and drawer cell context). */
export type OptimisticMatrixCell = Pick<
  IWorkJourneyMatrixCell,
  'kind' | 'code' | 'leaveType' | 'observation' | 'attendanceCompleted' | 'attendance'
>;

const toTimestampLike = (millis?: number): TimestampLike | undefined =>
  millis == null ? undefined : { _seconds: Math.floor(millis / 1000), _nanoseconds: 0 };

const toSeconds = (millis?: number): number | undefined =>
  millis == null ? undefined : Math.floor(millis / 1000);

const isLeaveChange = (changes: CreateShiftChangesParams): boolean =>
  changes.changeType === SHIFT_CHANGE_TYPE.LEAVE;

const resolveLeaveType = (changes: CreateShiftChangesParams) =>
  changes.isExcused ? ATTENDANCE_LEAVE_TYPE.JUSTIFIED : ATTENDANCE_LEAVE_TYPE.UNJUSTIFIED;

export const applyShiftChangesToAttendanceRecord = (
  record: IAttendanceRecord,
  changes: CreateShiftChangesParams,
): IAttendanceRecord => {
  if (isLeaveChange(changes)) {
    return {
      ...record,
      leaveType: resolveLeaveType(changes),
      attendanceComplete: false,
    };
  }

  return {
    ...record,
    // Marking correction implies the worker was present — clear any prior leave.
    leaveType: ATTENDANCE_LEAVE_TYPE.NONE,
    attendanceComplete: true,
    actualStartAt: toTimestampLike(changes.actualStartAt) ?? record.actualStartAt,
    actualEndAt: toTimestampLike(changes.actualEndAt) ?? record.actualEndAt,
    actualBreakStartAt: toTimestampLike(changes.actualBreakStartAt) ?? record.actualBreakStartAt,
    actualBreakEndAt: toTimestampLike(changes.actualBreakEndAt) ?? record.actualBreakEndAt,
  };
};

export const applyShiftChangesToMatrixCell = <TCell extends OptimisticMatrixCell>(
  cell: TCell,
  changes: CreateShiftChangesParams,
): TCell => {
  if (isLeaveChange(changes)) {
    const leaveType = resolveLeaveType(changes);
    const mapped = mapLeaveTypeToMatrixCell(leaveType);

    return {
      ...cell,
      kind: mapped?.kind ?? cell.kind,
      code: mapped?.code ?? cell.code,
      leaveType,
      attendanceCompleted: false,
      attendance: cell.attendance
        ? {
            ...cell.attendance,
            leaveType,
            attendanceComplete: false,
            hasActualStart: false,
          }
        : { leaveType, attendanceComplete: false, hasActualStart: false },
    };
  }

  const startSeconds = toSeconds(changes.actualStartAt);
  const endSeconds = toSeconds(changes.actualEndAt);
  const hasActualStart = startSeconds != null || cell.attendance?.hasActualStart === true;
  const isReplacementCell = cell.kind === 'replacement' || cell.code === 'REM';

  // Time adjustment with a start mark → present, regardless of the previous
  // chip (planned WORK, AUI, AUJ, …). Matching backend: completed attendance
  // resolves to PRE once the trigger finishes. Coverage days keep REM.
  if (!hasActualStart) {
    // End-only (or empty) correction still clears leave when converting from
    // absence — matches backend isPresenceCorrection (start OR end).
    if (endSeconds != null && (cell.leaveType || cell.kind === 'justified_absence' || cell.kind === 'unjustified_absence')) {
      return {
        ...cell,
        ...(isReplacementCell
          ? { kind: 'replacement' as const, code: 'REM' as const }
          : { kind: 'work' as const, code: 'PRE' as const }),
        leaveType: ATTENDANCE_LEAVE_TYPE.NONE,
        attendanceCompleted: false,
        attendance: {
          ...cell.attendance,
          leaveType: ATTENDANCE_LEAVE_TYPE.NONE,
          hasActualStart: cell.attendance?.hasActualStart === true,
          attendanceComplete: false,
          actualStartAtSeconds: cell.attendance?.actualStartAtSeconds ?? null,
          actualEndAtSeconds: endSeconds,
        },
      };
    }
    return {
      ...cell,
      attendance: {
        ...cell.attendance,
        actualStartAtSeconds: startSeconds ?? cell.attendance?.actualStartAtSeconds ?? null,
        actualEndAtSeconds: endSeconds ?? cell.attendance?.actualEndAtSeconds ?? null,
      },
    };
  }

  if (isReplacementCell) {
    return {
      ...cell,
      kind: 'replacement',
      code: 'REM',
      leaveType: ATTENDANCE_LEAVE_TYPE.NONE,
      attendanceCompleted: true,
      attendance: {
        ...cell.attendance,
        leaveType: ATTENDANCE_LEAVE_TYPE.NONE,
        hasActualStart: true,
        attendanceComplete: true,
        actualStartAtSeconds: startSeconds ?? cell.attendance?.actualStartAtSeconds ?? null,
        actualEndAtSeconds: endSeconds ?? cell.attendance?.actualEndAtSeconds ?? null,
      },
    };
  }

  return {
    ...cell,
    kind: 'work',
    code: 'PRE',
    leaveType: ATTENDANCE_LEAVE_TYPE.NONE,
    attendanceCompleted: true,
    attendance: {
      ...cell.attendance,
      leaveType: ATTENDANCE_LEAVE_TYPE.NONE,
      hasActualStart: true,
      attendanceComplete: true,
      actualStartAtSeconds: startSeconds ?? cell.attendance?.actualStartAtSeconds ?? null,
      actualEndAtSeconds: endSeconds ?? cell.attendance?.actualEndAtSeconds ?? null,
    },
  };
};

export const applyShiftChangesToMatrixCells = (
  cells: IWorkJourneyMatrixCell[],
  target: { userId: string; date: string },
  changes: CreateShiftChangesParams,
): IWorkJourneyMatrixCell[] =>
  cells.map(cell =>
    cell.userId === target.userId && cell.date === target.date
      ? applyShiftChangesToMatrixCell(cell, changes)
      : cell,
  );
